"use client";
import { forwardRef, useCallback } from "react"

import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import {
  exportEditorMarkdown,
  pickAndImportMarkdown,
} from "@/tiptap/core/markdown/markdown-io"

import { huduIconButtonClass } from "@/tiptap/lib/hudu-icon-button"
import { Button } from "@/tiptap/ui-primitive/button"

function DownloadIcon(props) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

function UploadIcon(props) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

function resolveExportFilename() {
  if (typeof document !== "undefined" && document.title) {
    return document.title
  }

  return "document"
}

export const MarkdownImportButton = forwardRef(
  ({ editor: providedEditor, onClick, className, ...buttonProps }, ref) => {
    const { editor } = useTiptapEditor(providedEditor)

    const handleClick = useCallback(
      (event) => {
        onClick?.(event)
        if (event.defaultPrevented || !editor) return
        pickAndImportMarkdown(editor)
      },
      [editor, onClick],
    )

    return (
      <Button
        type="button"
        variant="ghost"
        role="button"
        tabIndex={-1}
        aria-label="Import markdown"
        tooltip="Import markdown"
        className={huduIconButtonClass(className)}
        unstyled
        onClick={handleClick}
        ref={ref}
        {...buttonProps}
      >
        <DownloadIcon className="tiptap-button-icon" />
      </Button>
    )
  },
)

MarkdownImportButton.displayName = "MarkdownImportButton"

export const MarkdownExportButton = forwardRef(
  ({ editor: providedEditor, filename, onClick, className, ...buttonProps }, ref) => {
    const { editor } = useTiptapEditor(providedEditor)

    const handleClick = useCallback(
      (event) => {
        onClick?.(event)
        if (event.defaultPrevented || !editor) return
        exportEditorMarkdown(editor, {
          filename: filename ?? resolveExportFilename(),
        })
      },
      [editor, filename, onClick],
    )

    return (
      <Button
        type="button"
        variant="ghost"
        role="button"
        tabIndex={-1}
        aria-label="Export markdown"
        tooltip="Export markdown"
        className={huduIconButtonClass(className)}
        unstyled
        onClick={handleClick}
        ref={ref}
        {...buttonProps}
      >
        <UploadIcon className="tiptap-button-icon" />
      </Button>
    )
  },
)

MarkdownExportButton.displayName = "MarkdownExportButton"
